import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-material.css'
import { ColDef, RowNode } from 'ag-grid-community'
import { KTSVG, toAbsoluteUrl } from '../../../../_metronic/helpers'
import { agConfigSideBar, tableCellStyle } from '../../../../utils/AgGridConfig'
import Toast, { ToastType } from '../../../../utils/toast'
import { DeleteConfirmPopup } from '../../../../utils/DeleteConfirmPopup'
import {
  useGetAllUsersOfAccountQuery,
  useGetAllFundsOfUserQuery,
  useRemoveCompaniesOfInvestorMutation,
  useGetExistingCompaniesOfInvestorQuery,
  useGetExistingHissaCompaniesQuery,
  SyncCompanyInput,
} from '../../../../generated/graphql'
import { graphqlRequestClient, queryClient } from '../../../../queries/client'

interface RemoveCompaniesProps {
  companies: SyncCompanyInput[]
  selectedUserId: string | undefined
  selectedFundId: number | null
  isLoading: boolean
  accountId: string
}

const CompanyNameRender = memo((props: any) => (
  <span className='cursor-pointer text-hover-primary'>{props.value}</span>
))

const RemoveCompanies: React.FC<RemoveCompaniesProps> = ({
  companies,
  selectedUserId,
  selectedFundId,
  accountId,
}) => {
  const [localCompanies, setLocalCompanies] = useState<SyncCompanyInput[]>(companies)
  const [loading, setLoading] = useState<boolean>(false)
  const [isColumnOpen, setIsColumnOpen] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isFilter, setIsFilter] = useState(false)
  const [selectedRows, setSelectedRows] = useState<SyncCompanyInput[]>([])
  const [rowCount, setRowCount] = useState(companies.length)
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [selectedCompany, setSelectedCompany] = useState<SyncCompanyInput[] | null>(null)

  const isMultipleSelection = selectedCompany && selectedCompany.length > 1
  const singleCompanyName =
    selectedCompany && selectedCompany.length === 1 ? selectedCompany[0].companyName : ''

  const gridApi = useRef<any>(null)

  useEffect(() => {
    setLocalCompanies(companies)
    setRowCount(companies.length)
  }, [companies])
const removeCompanyMutation = useRemoveCompaniesOfInvestorMutation(graphqlRequestClient,{
  onSuccess: async () => {
    queryClient.invalidateQueries(useRemoveCompaniesOfInvestorMutation.getKey())
    queryClient.invalidateQueries(useGetExistingCompaniesOfInvestorQuery.getKey({input:{investorId:selectedFundId!}}))
    queryClient.invalidateQueries(useGetAllFundsOfUserQuery.getKey({input:{accountId:Number(accountId),userId:Number(selectedUserId)}}))
    Toast('Companies Removed Successfully!', ToastType.success)
  } ,
  onError: (error: any) => {
    console.error('Error removing companies:', error)
    Toast('Failed to remove companies. Please try again.', ToastType.error)
  },
})
  const openToolPanel = (key: any) => {
    if (key === 'columns') {
      if (gridApi.current) {
        if (!isColumnOpen) gridApi.current.api.openToolPanel(key)
        else gridApi.current.api.closeToolPanel()
        setIsColumnOpen((state) => !state)
        setIsFilterOpen(false)
      }
    } else if (key === 'filters') {
      if (gridApi.current) {
        if (!isFilterOpen) gridApi.current.api.openToolPanel(key)
        else gridApi.current.api.closeToolPanel()
        setIsFilterOpen((state) => !state)
        setIsColumnOpen(false)
      }
    }
  }

  const handleRemoveCompany = () => {
    if (!selectedFundId || !selectedCompany || !selectedUserId) {
      Toast('Please select a fund and company to remove', ToastType.error)
      return
    }

    setLoading(true)
    removeCompanyMutation.mutate(
      {
        input: {
          investorId: selectedFundId,
          userId: Number(selectedUserId),
          companies: selectedCompany,
        },
      },
      {
        onSettled: () => {
          // Optimistically update local state
          setLocalCompanies((prevCompanies) =>
            prevCompanies.filter(
              (company) =>
                !selectedCompany.some((selected) => selected.companyId === company.companyId)
            )
          )

          // Reset selection states
          setSelectedRows([])
          setLoading(false)
          setShowConfirmDelete(false)
          setSelectedCompany(null)

          // Deselect all rows in the grid
          if (gridApi.current?.api) {
            gridApi.current.api.deselectAll()
          }
        },
      }
    )
  }

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: { suppressColumnFilter: true },
      filterParams: { buttons: ['reset'], maxNumConditions: 5 },
      minWidth: 150,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    []
  )

  const componentsRegistry = useMemo(
    () => ({
      companyName: memo(CompanyNameRender),
    }),
    []
  )

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: 'Company Name',
        field: 'companyName',
        cellRenderer: 'companyName',
        cellStyle: tableCellStyle,
        initialWidth: 200,
        minWidth: 350,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        tooltipField: 'companyName',
        checkboxSelection: true,
        headerCheckboxSelection: true,
      },

      {
        headerName: '',
        field: 'actions',
        hide: false,
        width: 90,
        pinned: 'right',
        maxWidth: 90,
        filter: false,
        menuTabs: [],
        colId: 'action-column',
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        resizable: true,
        sortable: false,
        cellRenderer: (params: any) => (
          <i
            className='bi bi-trash3 text-danger bg-hover-light-danger p-2 px-3 rounded-circle cursor-pointer '
            style={{ fontSize: '18px', width: '18px', height: '18px' }}
            onClick={() => {
              setSelectedCompany([params.data])
              setShowConfirmDelete(true)
            }}
            title='Remove company from fund'
          />
        ),
      },
    ],
    []
  )

  const rowData = useMemo(
    () =>
      localCompanies.map((company) => ({
        companyId: company.companyId,
        companyName: company.companyName,
        companyType: company.companyType,
      })),
    [localCompanies]
  )

  const handleSearch = (e: any) => {
    const filterValue = e.target.value
    setIsFilter(!!filterValue)
    if (gridApi.current) {
      gridApi.current.api.setQuickFilter(filterValue)
      gridApi.current.api.deselectAll()
      setSelectedRows([])
      const displayedRowCount = gridApi.current.api.getDisplayedRowCount()
      setRowCount(displayedRowCount)
    }
  }

  const onSelectionChanged = () => {
    const selectedNodes = gridApi.current?.api?.getSelectedNodes() || []
    const renderedNodes: RowNode[] = gridApi.current?.api?.getRenderedNodes() || []

    if (!isFilter) {
      const selectedRowData = selectedNodes.map((node: RowNode) => node.data)
      setSelectedRows(selectedRowData)
      return
    }

    const visibleSelectedRows = renderedNodes
      .filter((node: RowNode) => node.isSelected())
      .map((node: RowNode) => node.data)
    setSelectedRows(visibleSelectedRows)
  }

  const getFilteredData = () => {
    const filteredData: any[] = []
    gridApi.current?.api.forEachNodeAfterFilter((node: any) => {
      filteredData.push(node.data)
    })
    setRowCount(filteredData.length)
    setIsFilter(localCompanies.length !== filteredData.length)
    return filteredData
  }

  return (
    <div className='p-0'>
      <div className='card'>
        {!selectedFundId ? (
          <div className='text-center py-10'>
            <img
              alt='no fund selected'
              src={toAbsoluteUrl('/media/custom-images/emptyRecentCompanies.svg')}
              className='h-150px my-6'
            />
            <h4 className='text-gray-600'>Please select a fund to view companies</h4>
          </div>
        ) : (
          <>
            <div className='d-flex flex-row justify-content-between align-items-center border-0 py-3 px-1'>
              <div>
                <h3 className='mb-1'>Companies</h3>
                {rowCount > 0 && (
                  <p className='fs-7 text-gray-500 mb-0'>
                    {rowCount} {rowCount > 1 ? 'Companies' : 'Company'}
                  </p>
                )}
              </div>

              <div className='d-flex flex-row align-items-center ms-auto gap-3'>
                <span
                  className='d-flex flex-column border border-1 border-secondary-txt bg-transparent h-38px items-center w-full rounded'
                  style={{
                    boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)',
                    border: '1px solid #F1F1F2',
                    zIndex: 6,
                  }}
                >
                  <span className='d-flex flex-row align-items-center'>
                    <KTSVG
                      path='media/icons/duotune/general/gen021.svg'
                      className='ps-4 svg-icon-2x text-start fs-9'
                      svgClassName='text-gray-700'
                    />
                    <input
                      type='search'
                      placeholder='Search by Company Name'
                      className='form-control w-auto mw-200-px border-0 h-35px rounded'
                      onChange={handleSearch}
                    />
                  </span>
                </span>

                <button
                  type='button'
                  onClick={() => openToolPanel('columns')}
                  className='px-4 rounded border border-1 border-secondary-txt bg-transparent h-38px fw-normal text-hover-primary'
                >
                  <KTSVG
                    path='/media/icons/duotune/layouts/lay004.svg'
                    className='svg-icon-3 text-secondary-txt me-1'
                  />
                  Columns
                </button>

                <button
                  type='button'
                  onClick={() => openToolPanel('filters')}
                  className='px-4 rounded border border-1 border-secondary-txt bg-transparent h-38px fw-normal text-hover-primary'
                >
                  <KTSVG
                    path='/media/icons/duotune/general/gen031.svg'
                    className='svg-icon-3 text-secondary-txt me-1'
                  />
                  Filter
                </button>

                {selectedRows.length > 1 && (
                  <button
                    className={`btn btn-danger d-flex align-items-center justify-content-center px-4 h-38px me-1 ${
                      loading ? 'disabled' : ''
                    }`}
                    disabled={loading}
                    onClick={() => {
                      setSelectedCompany(selectedRows)
                      setShowConfirmDelete(true)
                    }}
                  >
                    {loading ? (
                      <>
                        <span className='spinner-border spinner-border-sm me-2' />
                        Removing...
                      </>
                    ) : (
                      `Remove Selected (${selectedRows.length})`
                    )}
                  </button>
                )}
              </div>
            </div>

            <div
              className='card-body w-100 px-1 pt-0 ag-theme-material'
              style={{ height: '250px' }}
            >
              <AgGridReact
                sideBar={agConfigSideBar}
                onGridReady={(params) => {
                  gridApi.current = params
                }}
                onFilterChanged={getFilteredData}
                components={componentsRegistry}
                alwaysShowHorizontalScroll
                alwaysShowVerticalScroll
                alwaysMultiSort
                animateRows
                defaultColDef={defaultColDef}
                rowData={rowData}
                columnDefs={columnDefs}
                domLayout='normal'
                pagination={false}
                rowSelection='multiple'
                rowMultiSelectWithClick
                onSelectionChanged={onSelectionChanged}
                tooltipShowDelay={50}
                tooltipInteraction
                rowClass='border-right-0 border-left-0 border-dashed'
                getRowStyle={(params) => ({
                  background: params.rowIndex % 2 === 0 ? '#f8f8f8' : '#ffffff',
                })}
              />
            </div>

            {rowCount === 0 && (
              <div className='text-center py-10'>
                <img
                  alt='no companies'
                  src={toAbsoluteUrl('/media/custom-images/emptyRecentCompanies.svg')}
                  className='h-150px my-6'
                />
                <h5 className='text-gray-600'>No companies found for the selected fund.</h5>
              </div>
            )}
            <DeleteConfirmPopup
              showModel={showConfirmDelete}
              setShowModel={setShowConfirmDelete}
              loading={loading}
              title={isMultipleSelection ? 'Remove Companies' : 'Remove Company'}
              description={`Are you sure you want to remove ${
                isMultipleSelection
                  ? `${selectedCompany?.length} companies`
                  : `"${singleCompanyName}"`
              } from this fund?`}
              notes='This will remove the company for all users who have access to this fund and cannot be undone.'
              confirmText={
                isMultipleSelection
                  ? 'Type "DELETE COMPANIES" to confirm'
                  : `Type the company name "${singleCompanyName}" to confirm`
              }
              currentValue={isMultipleSelection ? 'DELETE COMPANIES' : singleCompanyName!}
              primaryButtonText='Remove'
              secondaryButtonText='Cancel'
              primaryAction={handleRemoveCompany}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default RemoveCompanies
